import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { mediaUrl, TRAINER_COLOR_PRESETS } from "../../../utils/clientHeroTheme";
import { personDisplayName } from "../../../utils/clientAppUtils";
import { useClientRefresh } from "../../../hooks/useClientRefresh";
import { usePlanAccess } from "../../../hooks/usePlanAccess";
import { Avatar, Skeleton } from "../../ui";
import s from "./clientApp.module.css";

function themeAccent(themeId) {
  return TRAINER_COLOR_PRESETS.find((p) => p.id === themeId)?.color || null;
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ClientMembers() {
  const plan = usePlanAccess();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMembers = useCallback(async () => {
    try {
      const data = await clinicAPI.listSocialMembers({
        q: query.trim() || undefined,
      });
      setMembers(data.members || []);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    setLoading(true);
    loadMembers();
  }, [loadMembers]);

  useClientRefresh(loadMembers);

  if (!plan.memberSocial) {
    return <Navigate to="/app/profile" replace />;
  }

  return (
    <div className={s.communityPage}>
      <header className={s.communityHead}>
        <button
          type="button"
          className={s.communityBack}
          onClick={() => navigate(-1)}
        >
          ← Назад
        </button>
        <h1 className={s.communityTitle}>Участники</h1>
        <p className={s.communityLead}>
          Спортсмены из ваших групп: смотрите профили и подписывайтесь на
          интересных людей.
        </p>
      </header>

      <div className={s.communityTip} role="note">
        <span className={s.communityTipIcon} aria-hidden>
          💡
        </span>
        <p>
          <strong>Лента клуба:</strong> все публикации — и в разделе «Клуб», и
          записи со стен участников — собраны в одной ленте. Свою стену и
          оформление профиля настраиваете в{" "}
          <Link to="/app/profile/account/social" className={s.communityTipLink}>
            Профиле → Оформление
          </Link>
          .
        </p>
      </div>

      <div className={s.communitySearch}>
        <SearchIcon />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по имени…"
          aria-label="Поиск участников"
          className={s.communitySearchInput}
        />
      </div>

      {loading ? (
        <div className={s.communitySkeleton}>
          <Skeleton height={72} style={{ borderRadius: 16 }} />
          <Skeleton height={72} style={{ borderRadius: 16 }} />
        </div>
      ) : members.length === 0 ? (
        <div className={s.communityEmpty}>
          <span className={s.communityEmptyIcon} aria-hidden>
            🧒
          </span>
          <p className={s.communityEmptyTitle}>Пока никого нет</p>
          <p className={s.communityEmptyHint}>
            Когда вас добавят в группу, здесь появятся другие спортсмены
          </p>
        </div>
      ) : (
        <ul className={s.communityList}>
          {members.map((m) => {
            const accent = themeAccent(m.profile_theme);
            return (
              <li key={m.id}>
                <Link
                  to={`/app/members/${m.id}`}
                  className={s.communityRow}
                  style={accent ? { "--member-accent": accent } : undefined}
                >
                  <Avatar
                    name={personDisplayName(m.full_name)}
                    src={mediaUrl(m.photo_url)}
                    size="md"
                    className={s.communityRowAvatar}
                  />
                  <div className={s.communityRowBody}>
                    <div className={s.communityRowTop}>
                      <strong className={s.communityRowName}>
                        {personDisplayName(m.full_name)}
                        {m.is_self ? (
                          <span className={s.communityYouBadge}>Вы</span>
                        ) : null}
                      </strong>
                      {!m.is_self && m.is_following ? (
                        <span className={s.communityFollowBadge}>Подписка</span>
                      ) : null}
                    </div>
                    <p className={s.communityRowSub}>
                      {m.profile_bio
                        ? m.profile_bio.slice(0, 80) +
                          (m.profile_bio.length > 80 ? "…" : "")
                        : m.is_self
                          ? "Ваш публичный профиль"
                          : "Профиль, стена и достижения"}
                    </p>
                  </div>
                  <span className={s.communityRowChevron} aria-hidden>
                    <ChevronIcon />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
